<template>
	<div v-if="blok.image_link">
		<nuxt-link
			:to="linkPath(blok.image_link)"
			class="flex flex-col items-center"
			:aria-label="blok.image.alt || blok.image.title"
		>
			<NuxtImg
				v-if="blok.image.filename"
				:src="blok.image.filename"
				:loading="blok.lazyLoading ? 'lazy' : 'eager'"
				:preload="!blok.lazyLoading"
				format="webp"
				provider="storyblok"
				:class="`min-h-4 min-w-4 ${storyblokImgDimensions.width ? '' : extraClasses} max-w-full ${imageClass || ''}`"
				:alt="blok.image.alt"
				:width="storyblokImgDimensions.width"
				:height="storyblokImgDimensions.height"
				:style="{ maxHeight: imageMaxHeight ? `${imageMaxHeight}px` : 'unset' }"
				:fit="isSvg ? undefined : 'in'"
				:placeholder="
					!isSvg &&
					img(
						blok.image.filename,
						{
							width: 10,
							height: 10,
							format: 'webp',
							quality: 50,
							filters: { blur: 2 }
						},
						{ provider: 'storyblok' }
					)
				"
			/>
		</nuxt-link>
	</div>
	<div v-else>
		<NuxtImg
			v-if="blok.image.filename"
			:src="blok.image.filename"
			:loading="blok.lazyLoading ? 'lazy' : 'eager'"
			:preload="!blok.lazyLoading"
			format="webp"
			provider="storyblok"
			:class="`min-h-4 min-w-4 ${storyblokImgDimensions.width ? '' : extraClasses} max-w-full ${imageClass || ''}`"
			:alt="blok.image.alt"
			:width="storyblokImgDimensions.width"
			:height="storyblokImgDimensions.height"
			:style="{ maxHeight: imageMaxHeight ? `${imageMaxHeight}px` : 'unset' }"
			:fit="isSvg ? undefined : 'in'"
			:placeholder="
				!isSvg &&
				img(
					blok.image.filename,
					{
						width: 10,
						height: 10,
						format: 'webp',
						quality: 50,
						filters: { blur: 2 }
					},
					{ provider: 'storyblok' }
				)
			"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { BlokImage } from "~/types/storyblok/storyblok";
import { imageDimension } from "~/layers/base/utils/imageDimension";

const img = useImage();

interface Props {
	blok: BlokImage;
	extraClasses?: string;
	imageSize?: string;
	imageMaxHeight?: string;
	/** Classes applied unconditionally (unlike `extraClasses`, which is
	 * dropped when explicit dimensions are set). */
	imageClass?: string;
}
const { blok, imageSize, imageMaxHeight, imageClass } = defineProps<Props>();
const { linkPath } = useStoryblokLink();

// Storyblok rasterises an SVG to a 1x PNG once any `/m/` modifier is added
// (`fit`, the blur placeholder), so serve SVGs untouched to keep them sharp.
const isSvg = computed(() => /\.svg$/i.test(blok.image?.filename ?? ""));

const storyblokImgDimensions = computed(() => {
	if (imageSize) {
		return { width: imageDimension(imageSize), height: undefined };
	}
	return {
		width: imageDimension(blok.maxWidth),
		height: imageDimension(blok.maxHeight)
	};
});
</script>
